import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OutputEmitterRef,
  Signal,
  WritableSignal,
  inject,
  output,
  signal,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { LetDirective } from '@ngrx/component';
import { Observable } from 'rxjs';

import { Pagination, PaginationMetadata } from '@core/shared/domain';
import { ReactiveMap } from '@core/shared/util';
import { DropdownComponent, PaginatorComponent, SelectOption } from '@core/ui';
import {
  PlattformPackungseinheit,
  PlattformPackungseinheitMapping,
} from '@mp/content-manager/industriekataloge/data-access';

import { PackungseinheitenColumnName } from '../detail/industriekatalog-tabs/column-name';
import { PackungseinheitenSelectorComponent } from '../packungseinheiten-selector/packungseinheiten-selector.component';

export interface PackungsUpdateEinheit {
  einheit: PlattformPackungseinheitMapping;
  mappedShortName?: string;
}

export interface PackungseinheitenDialogData {
  einheiten$: Observable<PlattformPackungseinheitMapping[]>;
  einheitenPagination$: Observable<PaginationMetadata>;
  optionen: PlattformPackungseinheit[];
  datasources: string[];
}

@Component({
  selector: 'mp-packungseinheiten-dialog-content',
  templateUrl: 'packungseinheiten-dialog-content.component.html',
  styleUrl: './packungseinheiten-dialog-content.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LetDirective,

    MatIconButton,
    MatDialogTitle,
    MatDialogContent,
    MatDialogClose,
    MatIcon,
    MatTableModule,

    DropdownComponent,
    PackungseinheitenSelectorComponent,
    PaginatorComponent,
  ],
})
export class PackungseinheitenDialogContentComponent implements OnInit {
  private readonly data: PackungseinheitenDialogData = inject(MAT_DIALOG_DATA);

  readonly pageCount: Signal<number> = signal<number>(10);
  readonly einheitenCols: WritableSignal<PackungseinheitenColumnName[]> = signal<PackungseinheitenColumnName[]>([
    'einheit',
    'artikel',
    'bezugsquelle',
  ]);
  readonly tableData: Observable<PlattformPackungseinheitMapping[]> = this.data.einheiten$;
  readonly optionen: PlattformPackungseinheit[] = this.data.optionen;
  readonly datasourcesOptions: SelectOption<string>[] = this.data.datasources.map((datasource) => ({
    label: datasource,
    value: datasource,
  }));
  readonly assignedEinheit: WritableSignal<ReactiveMap<string, string | undefined>> = signal<
    ReactiveMap<string, string | undefined>
  >(new ReactiveMap<string, string | undefined>({}));

  readonly initialDataSourceOption: string | undefined = this.datasourcesOptions.length
    ? this.datasourcesOptions[0].value
    : undefined;

  readonly einheitenPagination$: Observable<PaginationMetadata> = this.data.einheitenPagination$;
  readonly einheitenMappingSaved: OutputEmitterRef<PackungsUpdateEinheit> = output<PackungsUpdateEinheit>();
  readonly emitDatasourceChanged: OutputEmitterRef<string | undefined> = output<string | undefined>();
  readonly pageEvent: OutputEmitterRef<Pagination> = output<Pagination>();

  ngOnInit(): void {
    this.datasourceChanged(this.initialDataSourceOption);
  }

  datasourceChanged(datasource: string | undefined): void {
    this.emitDatasourceChanged.emit(datasource);
  }

  emitEinheitChanged(einheit: PlattformPackungseinheitMapping, value: PlattformPackungseinheit | null): void {
    this.assignedEinheit().set(einheit.shortName, value?.shortName);
  }

  emitPageChanges(pageChangePagination: Pagination): void {
    this.pageEvent.emit(pageChangePagination);
  }

  deleteEinheitsMapping(einheit: PlattformPackungseinheitMapping): void {
    this.einheitenMappingSaved.emit({ einheit });
    this.assignedEinheit().unset(einheit.shortName);
  }

  saveEinheit(einheit: PlattformPackungseinheitMapping): void {
    const mappedShortName = this.assignedEinheit().get(einheit.shortName);
    this.einheitenMappingSaved.emit({ einheit, mappedShortName });
    this.assignedEinheit().unset(einheit.shortName);
  }
}
